/**
 * Client-side typed lifecycle event emitter.
 *
 * Mirrors `server/lib/lifecycleEvents.ts` for browser-observed events.
 * Single call site for all client lifecycle events. Wraps `rsAnalytics.track`
 * (which fans out to PostHog + GA4 via Rudderstack). Never throws.
 *
 * Source of truth: `docs/analytics/event-taxonomy.md`.
 */
import { rsAnalytics } from './rudderstack';

export type ClientLifecycleEvent =
  | 'signup.started'
  | 'plan.viewed'
  | 'plan.selected'
  | 'checkout.redirected_to_stripe'
  | 'checkout.completed'
  | 'checkout.client_state_pending'
  | 'checkout.client_state_confirmed'
  | 'number.search'
  | 'number.selected'
  | 'voice.agent_loaded'
  | 'voice.agent_load_failed'
  | 'mic.permission_granted'
  | 'mic.permission_denied'
  | 'exit_survey.shown'
  | 'exit_survey.submitted';

export function trackClientEvent(
  event: ClientLifecycleEvent,
  properties: Record<string, unknown> = {},
): void {
  try {
    const [category, ...rest] = event.split('.');
    const action = rest.join('.');
    rsAnalytics.track(event, {
      category,
      action,
      ...properties,
    });
  } catch (err) {
    const isDev =
      (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ||
      (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production');
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[lifecycleEvents] track failed', event, err);
    }
  }
}
